import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AppBar from '@material-ui/core/AppBar'
import { Theme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import clsx from 'clsx'
import WorkspaceActionBarWidgetContainer from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarWidgetContainer'
import { WorkspaceActionBarWidgetLabelOptions } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarWidgetLabel'
import React from 'react'

const transitionDuration = '100ms'

const useStyles = makeStyles((theme: Theme) => ({
    actionBar: {
        background: '#5E5F61',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0, 0),
        },
        transition: transitionDuration,
    },
    h1Text: {
        ...theme.typography.h1,
        fontSize: 30,
        [theme.breakpoints.up('xs')]: {
            padding: theme.spacing(0, 3),
        },
    },
    outerWidgetGroupWrapper: {
        display: 'inline-flex',
        justifyContent: 'space-between',
        width: '100%',
    },
    rightAlignedWidgetsWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    collapseExpandButton: {
        cursor: 'pointer',
        color: theme.palette.common.white,
        width: 60,
        alignItems: 'center',
        fontSize: pxToRem(18),
        borderRadius: 0,
        transition: transitionDuration,

        '& > .MuiButton-label': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            '& > p': {
                marginTop: -theme.spacing(1),
            },
        },
    },
    collapsedButton: {
        height: 40,
        marginTop: -theme.spacing(0.625),
        marginBottom: -theme.spacing(0.625),
    },
    expandedButton: {
        height: 90,
        marginTop: -theme.spacing(1.25),
        marginBottom: -theme.spacing(1.25),
    },
    collapsedBar: {
        minHeight: 40,
        maxHeight: 40,
        paddingTop: theme.spacing(0.625),
        paddingBottom: theme.spacing(0.625),
    },
    expandedBar: {
        minHeight: 60,
        maxHeight: 60,
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        paddingLeft: theme.spacing(2.5),
    },
    expandCollapseButtonLabel: {
        fontSize: pxToRem(18),
        marginTop: -theme.spacing(0.625),
    },
}))

export interface ReportsPageActionBarWidget {
    /**
     * Label shown on top of the action bar widget.
     * This is also the portion that remains displayed
     * when the action bar is "collapsed". If undefined,
     * the action bar will display the `component` when
     * collpsed instead.
     *
     * You can directly pass in the text to be displayed,
     * or optionally use the object form to pass in
     * a custom text alignment as well.
     *
     */
    label?: string | WorkspaceActionBarWidgetLabelOptions

    /**
     * Custom component rendered below the label
     */
    component: React.ReactNode

    /**
     * String key used to update the widget's state via the
     * context. No need to pass this in if the widget is
     * stateless.
     *
     * Use this for "counter"-type widgets.
     */
    stateKey?: string

    /* custom style to be applied on the widget container */
    customContainerStyle?: string
}
export interface ReportActionBarActionBarProps {
    left?: ReportsPageActionBarWidget[][]
    right?: ReportsPageActionBarWidget[][]
    title?: React.ReactNode
    onStateChange: (stateKey: string, newValue: any, persist?: boolean) => void
    currentState: Record<string, any>
}

const ReportsPageActionBar: React.FC<ReportActionBarActionBarProps> = ({
    left = [],
    right = [],
    title = undefined,
    currentState,
}) => {
    const classes = useStyles()

    return (
        <AppBar
            elevation={0}
            position="relative"
            className={clsx(
                'workspaceActionBar',
                classes.actionBar,
                classes.expandedBar
            )}
        >
            <ContainerMaxWidthXl>
                <div className={classes.outerWidgetGroupWrapper}>
                    {!!title ? (
                        title
                    ) : (
                        <WorkspaceActionBarWidgetContainer
                            showGroupBorder
                            widgets={left}
                            isCollapsed={false}
                        />
                    )}

                    <div className={classes.rightAlignedWidgetsWrapper}>
                        {
                            <WorkspaceActionBarWidgetContainer
                                showLeftBorder
                                widgets={right}
                                isCollapsed={false}
                            />
                        }
                    </div>
                </div>
            </ContainerMaxWidthXl>
        </AppBar>
    )
}

export default ReportsPageActionBar
